import { appRoutes } from '@fabric-app/business/app/routing/routes.config';
import { renderResourceIcon } from '@fabric-app/business/resource-detail/components/ResourceIcon/renderResourceIcon';
import { useQueryResourceAncestors } from '@fabric-app/business/resource-detail/queries/useQueryResourceAncestors';
import type { PathItem } from '@fabric-app/business/resources/components/ResourcePath/ResourcePath';
import { TopBarPath } from '@fabric-app/business/resources/components/ResourceTopBarPath';
import { getResourceNamePlaceholder } from '@fabric-app/business/resources/utils/getResourceTitle';
import type { ApiIntegrationWithViewConfig } from '@fabric-app/types/connections';
import type { ResourceDetail } from '@fabric-app/types/resources';
import { generatePath } from '@fabric-app/utils/url/routing';
import Link from 'next/link';
import { useMemo } from 'react';

interface ResourceTopBarPathProps {
  resource: ResourceDetail;
  integration: ApiIntegrationWithViewConfig;
}

export const IntegrationResourceTopBarPath: React.FC<ResourceTopBarPathProps> = ({
  resource,
  integration,
}) => {
  const { accessibleAncestors } = useQueryResourceAncestors(resource.id);

  const renderedPath = useMemo(() => {
    const res: PathItem[] = accessibleAncestors.map((item) => {
      return {
        id: item.id,
        name: item.name,
        href: generatePath(appRoutes.folder.detail, {
          folderId: item.id,
        }),
      };
    });

    if (!integration.viewConfig.isSingleMirror) {
      res.unshift({
        id: integration.id,
        name: integration.displayName,
        href: generatePath(appRoutes.integrations.detail.view, {
          integrationId: integration.id,
        }),
      });
    }

    res.push({
      id: resource.id,
      name: resource.name || getResourceNamePlaceholder(resource),
      icon: integration.viewConfig.isSingleMirror
        ? null
        : renderResourceIcon(resource, { size: 16 }),
    });

    return res;
  }, [accessibleAncestors, resource, integration]);

  return (
    <>
      <TopBarPath
        path={renderedPath}
        startElement={
          // for now only the google drive has it's own view
          integration.viewConfig.isSingleMirror ? (
            <integration.viewConfig.IconPath />
          ) : (
            <Link
              href={generatePath(appRoutes.integrations.detail.view, {
                integrationId: integration.id,
              })}
            >
              <integration.viewConfig.IconPath />
            </Link>
          )
        }
      />
    </>
  );
};
